// Global Instructions Rule Applied!
//
// Review a pending agent proposal in the New form it is destined for
// (owner-approved 2026-09-02).
//
// The problem it solves: a pending row used to give you a summary line and a
// raw JSON payload, and the only verbs were Approve and Reject. You approved on
// trust, could not correct a wrong company or add a figure the agent lacked,
// and a proposal missing a REQUIRED field was approved blind and died at the
// write as a `failed` row with a Postgres-shaped message.
//
// Why the real New form and not a bespoke viewer: every write tool applies its
// payload through the same controller the human New form posts to
// (create_deal → process_new_deal → POST /deals), so the payload already IS a
// New-form record. Rendering it in that form gets validation, lookups,
// required-field gating and the multi-entity sections (a deal's company,
// contacts, round, commitments and tasks) for free, and cannot drift from what
// the write will actually do.
//
// NOTHING IS PERSISTED UNTIL APPROVE. This is a client-side rendering of the
// payload — deliberately not a draft record, which would leak half-real rows
// into grids, counts and exports.
//
// The host trick is GlobalQuickAddDrawer's, verbatim in spirit: wrap the
// DrawerNew forms in overridden DrawerContext / RightMenuContext so they run as
// an app-level overlay with no /forms/drawer/ URL. What differs is the header,
// the footer verbs, and where submit goes.
//
// THE THREE VERBS. Reject and Cancel are both "no" and must not be confusable:
//   * Reject  — decide against it. The proposal is marked rejected and stays
//               in the list, so the decision (and who made it) is visible.
//   * Cancel  — close and decide later. The proposal STAYS PENDING; edits are
//               discarded, behind the unsaved-changes confirm if any were made.
//   * Approve — create the record now, from what is on screen.
//
// ON BEHALF OF (2026-09-02). An app admin reviews other users' proposals from
// Agent Oversight through this same drawer. The record is still created by
// whoever clicks Approve — there is no impersonation — so when the proposal
// belongs to somebody else the header and the alert say so plainly, rather
// than letting an admin approve a stranger's work believing it is their own.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button as AntButton, Modal, Space, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck, faBan, faRobot } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import Button from '../../../core/components/Button.jsx'
import { DrawerContext, type DrawerContextValue } from '../../../core/contexts/DrawerContext.jsx'
import { RightMenuContext } from '../../../core/contexts/RightMenuContext.jsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { Drawer as DealDrawerNew } from '../../deals/components/forms/DrawerNew.tsx'
import { Drawer as CompanyDrawerNew } from '../../companies/components/forms/DrawerNew.tsx'
import { Drawer as ContactDrawerNew } from '../../contacts/components/forms/DrawerNew.tsx'
import { Drawer as FundraisingDrawerNew } from '../../fundraising/components/forms/DrawerNew.tsx'
import { InteractionForm, type InteractionFormRef } from '../../interactions/forms/InteractionForm.tsx'
import {
  ENTITY_REVIEW_TITLE,
  TOOL_TO_ENTITY,
  hydrateCompanyRef,
  interactionFormValuesToRecord,
  interactionParentRef,
  interactionRecordToFormValues,
  type ProposalEntity
} from '../utils/proposalForms.ts'
import { fetchCompanyById } from '../../companies/services/companiesService.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import type { AgentProposal } from '../../../state_manager/data_models/agent_access_model.ts'

const noop = (): void => {}

export interface ProposalReviewDrawerProps {
  proposal: AgentProposal | null
  /** Approve with the record on screen. Resolves when the replay has finished. */
  onApprove: (payload: Record<string, any>) => Promise<void>
  /** Reject outright — the proposal is trashed. */
  onReject: () => Promise<void>
  /** Close and leave it pending. */
  onClose: () => void
  /** Open a record in a tab — used by the interaction's read-only parent link. */
  onOpenRecord?: (module: string, id: string) => void
  /**
   * The user this proposal was filed for, when that is NOT the reviewer — an
   * app admin assisting from Agent Oversight. null on one's own proposals,
   * which is every row of the My Agents screen.
   */
  onBehalfOf?: string | null
  busy?: boolean
}

export function ProposalReviewDrawer({
  proposal,
  onApprove,
  onReject,
  onClose,
  onOpenRecord,
  onBehalfOf = null,
  busy = false
}: ProposalReviewDrawerProps): React.JSX.Element | null {
  const formRef = useRef<{ submit: () => Promise<void> } | null>(null)
  const interactionRef = useRef<InteractionFormRef | null>(null)
  const [width, setWidth] = useState<string>('46%')
  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false)
  const [confirmRejectVisible, setConfirmRejectVisible] = useState(false)

  const tool = proposal?.agent_proposals__tool ?? ''
  const entity: ProposalEntity | undefined = TOOL_TO_ENTITY[tool]
  const filedPayload = (proposal?.agent_proposals__payload ?? {}) as Record<string, any>

  // The seed handed to the form.
  //
  // DERIVED AT RENDER, not assigned in an effect. The forms read their seed
  // exactly once — into useState / useRef — so the very first render must
  // already carry the payload. Seeding from an effect mounted them against an
  // empty object and they never caught up, which showed as a blank review form.
  //
  // `hydratedFor` scopes the hydrated copy to the proposal it was fetched for,
  // so opening a second proposal cannot briefly show the first one's company.
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  const [hydratedPayload, setHydratedPayload] = useState<Record<string, any> | null>(null)
  // The interaction parent's display name, resolved the same way and for the
  // same reason — a UUID tells the reviewer nothing about what this attaches to.
  const [parentLabel, setParentLabel] = useState<string | null>(null)
  const proposalId = proposal?.agent_proposals__id ?? null
  const payload =
    hydratedPayload && hydratedFor === proposalId ? hydratedPayload : filedPayload

  useEffect(() => {
    if (!proposalId || !entity) return undefined
    let cancelled = false
    setParentLabel(null)

    // A failed lookup is never surfaced: the field falls back to the raw id (or
    // a generic link label), which is poor but still correct and approvable.
    const companyName = async (id: string): Promise<string | null> => {
      try {
        const res = await fetchCompanyById(id, getAccessToken(store.getState().auth))
        const name = (res?.data as Record<string, any> | null)?.[AccountRecordFields.accounts__name]
        return typeof name === 'string' && name ? name : null
      } catch {
        return null
      }
    }

    void (async () => {
      const hydrated = await hydrateCompanyRef(entity, filedPayload, companyName)
      // Referential equality means nothing was hydrated — leave the derived
      // payload on the filed one rather than triggering a pointless remount.
      if (!cancelled && hydrated !== filedPayload) {
        setHydratedPayload(hydrated)
        setHydratedFor(proposalId)
      }

      if (entity === 'interaction') {
        const parent = interactionParentRef(filedPayload)
        // Only a company parent is resolved by name here; a deal or prospect
        // parent still gets a working link, just a generic label.
        if (parent?.module === 'companies') {
          const name = await companyName(parent.id)
          if (!cancelled && name) setParentLabel(name)
        }
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId, entity])

  // A FAILED proposal is reviewed here too (owner 2026-09-02): a bare retry
  // replays the payload that already failed, so the reason it failed is the
  // thing the reviewer most needs to see.
  const isRetry = proposal?.agent_proposals__status === 'failed'
  const failureText = isRetry
    ? ((proposal?.agent_proposals__result as Record<string, any> | null)?.error as string | undefined)
    : undefined

  const drawerContextValue = useMemo<DrawerContextValue>(
    () => ({
      isOpen: true,
      basePath: null,
      recordId: null,
      isNew: true,
      formType: entity ?? null,
      pendingNavigation: null,
      openDrawerForNew: noop,
      openDrawerForRecord: noop,
      closeDrawer: () => setConfirmCloseVisible(true),
      updateDrawerRecord: noop,
      confirmPendingNavigation: noop,
      cancelPendingNavigation: noop,
      getCurrentBasePath: () => '/'
    }),
    [entity]
  )

  const rightMenuContextValue = useMemo(
    () => ({ width, setRightMenuWidth: setWidth, resetRightMenuWidth: () => setWidth('46%') }),
    [width]
  )

  if (!proposal || !entity) return null

  // The DrawerNew forms hand their assembled record to onSubmit — already in the
  // canonical shape process_new_* consumes, which is the shape the proposal
  // payload is in. So approve just forwards it.
  const handleFormSubmit = async (formData: Record<string, any>): Promise<void> => {
    await onApprove(formData)
  }

  const handleApproveClick = (): void => {
    if (entity === 'interaction') {
      if (!interactionRef.current?.validateFields()) return
      const values = interactionRef.current.getValues() as unknown as Record<string, any>
      // Merged over the FILED payload, not the display seed: the parent links
      // and the pre-assigned id are not form fields and must survive verbatim.
      void onApprove(interactionFormValuesToRecord(filedPayload, values))
      return
    }
    void formRef.current?.submit?.()
  }

  const filedOn = proposal.agent_proposals__date_created
    ? dayjs(proposal.agent_proposals__date_created).format('MM/DD/YYYY')
    : null

  // The key changes when the proposal changes AND when hydration lands, which
  // is what makes the resolved company name appear: the drawers read their seed
  // once, so only a remount picks it up. Same trick the quick-add overlay uses
  // to get a fresh form per open.
  const formKey = `${entity}-${proposal.agent_proposals__id}-${hydratedFor ? 'hydrated' : 'filed'}`
  const formProps = { ref: formRef as any, onSubmit: handleFormSubmit, loading: busy }
  let formContent: React.ReactNode = null
  if (entity === 'deal') {
    formContent = <DealDrawerNew key={formKey} {...formProps} deal={payload} module='deals' />
  } else if (entity === 'company') {
    formContent = <CompanyDrawerNew key={formKey} {...formProps} company={payload as any} />
  } else if (entity === 'contact') {
    formContent = <ContactDrawerNew key={formKey} {...formProps} contact={payload as any} />
  } else if (entity === 'prospect') {
    formContent = <FundraisingDrawerNew key={formKey} {...formProps} prospect={payload} />
  } else if (entity === 'interaction') {
    // The parent is shown READ-ONLY. log_interaction refuses to build without
    // one, so there is always exactly one to name — and changing it would make
    // this a different proposal, not an edit of this one.
    const parent = interactionParentRef(filedPayload)
    formContent = (
      <>
        {parent ? (
          <FormSection title='Attaches to' titleColor='blue' scrollable={false} className='mb-2'>
            <div className='flex items-center gap-2 text-xs'>
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() => onOpenRecord?.(parent.module, parent.id)}
              >
                {parentLabel || 'Open the record'}
              </button>
              <span className='text-gray-400'>· fixed by the proposal</span>
            </div>
          </FormSection>
        ) : null}
        <InteractionForm
          key={formKey}
          ref={interactionRef}
          isAddMode
          initialValues={interactionRecordToFormValues(payload) as any}
        />
      </>
    )
  }

  return (
    <>
      <div className='fixed inset-0 z-[60] bg-black/30' onClick={() => setConfirmCloseVisible(true)} />
      <div
        data-component='ProposalReviewDrawer'
        className='fixed top-10 right-0 bottom-0 z-[61] bg-white border-l border-gray-200 shadow-lg transition-all duration-300 ease-in-out'
        style={{ width, minWidth: 460 }}
      >
        <div className='h-full flex flex-col'>
          <div
            className='flex items-center justify-between px-4 border-b border-gray-200 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-700 gap-2'
            style={{ height: '48px' }}
          >
            <div className='flex-1 min-w-0'>
              <h3 className='text-lg font-semibold text-white truncate'>
                {ENTITY_REVIEW_TITLE[entity]}
              </h3>
              <div className='text-white/85 text-xs truncate'>
                Drafted by {proposal.agent_proposals__agent_name || 'your agent'}
                {onBehalfOf ? ` for ${onBehalfOf}` : ''}
                {filedOn ? ` · ${filedOn}` : ''}
              </div>
            </div>
            <Tooltip title='Close'>
              <AntButton
                size='small'
                icon={<FontAwesomeIcon icon={faTimes} />}
                onClick={() => setConfirmCloseVisible(true)}
                className='bg-white/20 border-white/40 text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
                style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
              />
            </Tooltip>
          </div>

          <div className='flex-1 min-h-0 overflow-y-auto'>
            <div className='p-2 h-full'>
              <Alert
                showIcon
                type='info'
                icon={<FontAwesomeIcon icon={faRobot} />}
                style={{ marginBottom: 8 }}
                message={
                  onBehalfOf
                    ? `${proposal.agent_proposals__agent_name || 'An agent'} drafted this for ${onBehalfOf}`
                    : `${proposal.agent_proposals__agent_name || 'Your agent'} drafted this`
                }
                description={
                  isRetry
                    ? 'This write was attempted and failed, so nothing exists yet. Correct the cause below, then approve to try again.'
                    : onBehalfOf
                      ? `Nothing has been created yet. Check the details, correct anything that is wrong, then approve — the record is created at that moment, by you, on behalf of ${onBehalfOf}. It will show you as the approver.`
                      : 'Nothing has been created yet. Check the details, correct anything that is wrong, then approve — the record is created at that moment, by you.'
                }
              />

              {failureText ? (
                <Alert
                  showIcon
                  type='error'
                  style={{ marginBottom: 8 }}
                  message='Why it failed'
                  description={failureText}
                />
              ) : null}

              {proposal.agent_proposals__rationale ? (
                <FormSection title="The agent's reasoning" titleColor='blue' scrollable={false} className='mb-2'>
                  <span className='text-xs text-gray-600'>{proposal.agent_proposals__rationale}</span>
                </FormSection>
              ) : null}

              <DrawerContext.Provider value={drawerContextValue}>
                <RightMenuContext.Provider value={rightMenuContextValue}>
                  {formContent}
                </RightMenuContext.Provider>
              </DrawerContext.Provider>
            </div>
          </div>

          {/* House grammar: [destructive] [dismiss] [primary]. All three sit
              here by owner decision — Reject and Cancel mean different things,
              so the labels rather than the positions carry the difference. */}
          <div className='p-2 border-t border-gray-200 bg-gray-50 flex justify-end'>
            <Space>
              <Button
                variant='delete'
                icon={<FontAwesomeIcon icon={faBan} />}
                onClick={() => setConfirmRejectVisible(true)}
                disabled={busy}
                size='middle'
              >
                Reject
              </Button>
              <Button variant='cancel' onClick={() => setConfirmCloseVisible(true)} size='middle'>
                Cancel
              </Button>
              <Button
                variant='save'
                icon={<FontAwesomeIcon icon={faCheck} style={{ color: '#FFFFFF' }} />}
                onClick={handleApproveClick}
                loading={busy}
                size='middle'
              >
                {isRetry ? 'Approve & retry' : 'Approve & create'}
              </Button>
            </Space>
          </div>
        </div>

        <Modal
          title='Close without deciding?'
          open={confirmCloseVisible}
          onCancel={() => setConfirmCloseVisible(false)}
          closable={false}
          maskClosable={false}
          footer={
            <Space>
              <Button variant='cancel' onClick={() => setConfirmCloseVisible(false)}>
                Keep reviewing
              </Button>
              <Button
                variant='delete'
                onClick={() => {
                  setConfirmCloseVisible(false)
                  onClose()
                }}
              >
                Discard changes
              </Button>
            </Space>
          }
        >
          <p>
            The proposal stays <strong>pending</strong> and you can come back to it. Any edits you
            made here are discarded.
          </p>
        </Modal>

        <Modal
          title='Reject this proposal?'
          open={confirmRejectVisible}
          onCancel={() => setConfirmRejectVisible(false)}
          closable={false}
          maskClosable={false}
          footer={
            <Space>
              <Button variant='cancel' onClick={() => setConfirmRejectVisible(false)}>
                Keep reviewing
              </Button>
              <Button
                variant='delete'
                onClick={() => {
                  setConfirmRejectVisible(false)
                  void onReject()
                }}
              >
                Reject
              </Button>
            </Space>
          }
        >
          <p>
            Nothing will be created. The proposal stays in the list, marked{' '}
            <strong>rejected</strong>, so the decision and who made it stay visible
            {onBehalfOf ? ` to ${onBehalfOf}` : ''}. This is not the same as closing — use{' '}
            <strong>Cancel</strong> if you want to decide later.
          </p>
        </Modal>
      </div>
    </>
  )
}
